/**
 * DataGridPremium-backed replacement for DetailPanel.jsx. Rendered by
 * Table's getDetailPanelContent, so it only mounts when a row is expanded —
 * the lazy fetch therefore runs on mount (no getIsExpanded() gating needed).
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { ThemeContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  buildGridColumns,
  injectRowIds,
  makeGetRowId
} from './tableColumnAdapter';

function getBoxRows(rowData, boxKeys, detailKey) {
  if (boxKeys) {
    const data = {};
    for (const key in rowData) {
      if (boxKeys.includes(key)) data[key] = rowData[key];
    }
    return [data];
  }
  return rowData?.boxRows || rowData?.[detailKey] || [];
}

// Derive columns from the first row's keys when a consumer supplies boxKeys
// but no explicit boxColumns.
function deriveColumns(rows) {
  const first = rows?.[0];
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first).map((key) => ({ accessorKey: key, header: key }));
}

const Panel = styled(Box)(({ bgcolor }) => ({ backgroundColor: bgcolor }));

export default function DetailPanel({
  rowData,
  boxKeys,
  columns = [],
  detailKey,
  lazyFunction
}) {
  const { theme } = useContext(ThemeContext);
  const t = useAutoTranslation();

  const [rows, setRows] = useState(() =>
    getBoxRows(rowData, boxKeys, detailKey)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (typeof lazyFunction !== 'function') return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const result = await lazyFunction(rowData?._id, rowData?.status);
        if (!cancelled && result?.length > 0) setRows(result);
      } catch {
        if (!cancelled) setIsError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gridRows = useMemo(() => injectRowIds(rows || []), [rows]);
  const gridColumns = useMemo(() => {
    const source = columns && columns.length ? columns : deriveColumns(rows);
    return buildGridColumns(source, {});
  }, [columns, rows]);
  const getRowId = useMemo(() => makeGetRowId(), []);

  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  if (isError)
    return <Alert severity="error">{t('Error Loading User Info')}</Alert>;

  return (
    <Panel bgcolor={theme.appearance?.color} sx={{ p: 1, textAlign: 'center' }}>
      {gridRows && gridRows.length > 0 ? (
        <DataGridPremium
          rows={gridRows}
          columns={gridColumns}
          getRowId={getRowId}
          density="comfortable"
          hideFooter
          disableColumnMenu
          disableColumnFilter
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 'none',
            backgroundColor: theme.appearance?.color,
            color: theme.appearance?.text,
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme.accent + '55',
              color: '#000',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-cell': { fontSize: '0.9rem' }
          }}
        />
      ) : (
        t('No records to display')
      )}
    </Panel>
  );
}
